import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Multiselect, DropdownList } from 'react-widgets';
import Custom from '../Multiselects/Custom';
import DatePicker from './DataComponent__DatePicker';
import DynamicSelectSegment from '../DynamicSelectSegment';
import ButtonsList from '../ButtonsList';
import InputGroupMultiselect from '../InputGroupMultiselect';
import CustomNotify from '../Notify';
import Button from '../Button';
import debounce from '../../helpers/debounce';
import query from '../../helpers/query';
import config from '../../config';
import { useLocation } from 'react-router-dom';
import { I18nContext } from 'utils/I18n/context';

const initPage = config.initialPage;
const minLength = 3;
const templateKeys = {
    datepicker: 'getDatepicker',
    select: 'getSelect',
    multiselect: 'getMultiselect',
    dropdown: 'getDropdown',
    textInput: 'getTextInput',
    custom: 'getCustom',
    dynamic: 'getDynamic',
    goalTree: 'getSelectGoalTree',
    currency: 'getNull'
};

const DataComponentFilter = ({ 
    fields, 
    startDate, 
    endDate, 
    gray, 
    loading, 
    hideResetButton, 
    onAction 
}) => {
    const { t } = useContext(I18nContext);
    const location = useLocation();
    const [state, setState] = useState({
        selected: [],
        values: {}
    });

    const staticNames = t?.dataTable;
    const staticNamesActions = t?.actions || { 
        apply: 'Apply', 
        reset: 'Reset' 
    };

    const messagesForSelect = {
        emptyList: staticNames.emptyList
    };

    const getDefValues = useCallback((fields = []) => {
        let values = {};
        fields.map(item => {
            if (!item.value) return;
            if (item.name === 'datepicker') {
                item.value.startDate && (values[`${item.key}StartDate`] = item.value.startDate);
                item.value.endDate && (values[`${item.key}EndDate`] = item.value.endDate);
            } else {
                values[item.key] = item.value;
            }
        });
        return values;
    }, []);

    const getSelected = useCallback((fields = [], values = {}) => {
        let selected = [];
        fields.map((item, i) => {
            if ((item.name === 'datepicker' && values[`${item.key}StartDate`]) || values[item.key]) {
                selected.push({ ...item, fieldIndex: i });
            }
        });
        return selected;
    }, []);

    const getValue = useCallback((data) => {
        if (data.name === 'datepicker') {
            return {
                startDate: state.values[`${data.key}StartDate`] || moment().subtract(config.defaultDatepickerDaysPeriod, 'days'),
                endDate: state.values[`${data.key}EndDate`] || moment().endOf('day')
            };
        } else if (data.name === 'textInput') {
            return state.values[data.key] || '';
        }
        return state.values[data.key];
    }, [state.values]);

    const getNull = useCallback(() => null, []);

    const textInputChange = useCallback(debounce((name) => {
        if (state.values[name] && state.values[name].length > 0 && state.values[name].length <= minLength) {
            let message = t?.validate?.minLineLength?.replace(/%s/i, minLength) || `Minimum length is ${minLength}`;
            CustomNotify(message, 'error');
        }
    }, 500), [state.values, t]);

    const deleteValue = useCallback((values, item) => {
        if (item.name === 'datepicker') {
            delete values[`${item.key}StartDate`];
            delete values[`${item.key}EndDate`];
        } else {
            delete values[item.key];
        }
    }, []);

    const onDataPickerApply = useCallback((datepicker, key) => {
        setState(prev => ({
            ...prev,
            values: {
                ...prev.values,
                [`${key}StartDate`]: datepicker.startDate.clone().startOf('day'),
                [`${key}EndDate`]: datepicker.endDate.clone().endOf('day')
            }
        }));
    }, []);

    const onTextInputChange = useCallback((event) => {
        const name = event.target.name;
        setState(prev => ({
            ...prev,
            values: { ...prev.values, [name]: event.target.value }
        }));
        textInputChange(name);
    }, [textInputChange]);

    const onSelectChange = useCallback((event) => {
        const name = event.target.name;
        setState(prev => ({
            ...prev,
            values: { ...prev.values, [name]: event.target.value }
        }));
    }, []);

    const onChange = useCallback((value, key) => {
        setState(prev => ({
            ...prev,
            values: { ...prev.values, [key]: value }
        }));
        onActionHandler();
    }, []);

    const onChangeDynamic = useCallback((value, key) => {
        const newValue = value.map(item => ({ title: item.title, value: item.value }));
        setState(prev => ({
            ...prev,
            values: { ...prev.values, [key]: newValue }
        }));
    }, []);

    const onActionHandler = useCallback((type) => {
        setState(prev => ({
            ...prev,
            values: { ...prev.values, page: initPage }
        }));
        onAction(state.values, type);
    }, [onAction, state.values]);

    const onChangeFilter = useCallback((item, index) => {
        const fieldIndex = fields.findIndex(data => data.key === item.key);
        let selected = [...state.selected];
        let values = { ...state.values };
        let oldItem = selected[index];
        deleteValue(values, oldItem);
        selected[index] = { ...item, fieldIndex };
        setState(prev => ({ ...prev, selected, values }));
    }, [fields, state.selected, state.values, deleteValue]);

    const onAddFilter = useCallback((item) => {
        const fieldIndex = fields.findIndex(data => data.key === item.key);
        let selected = [...state.selected];
        selected.push({ ...item, fieldIndex });
        setState(prev => ({ ...prev, selected }));
    }, [fields, state.selected]);

    const onDelete = useCallback((item, index) => {
        let selected = [...state.selected];
        let values = { ...state.values };
        let deletedItem = selected.find(({ name }) => name === item.name);
        index = index === undefined ? selected.indexOf(deletedItem) : index;
        deleteValue(values, deletedItem);
        selected.splice(index, 1);
        setState(prev => ({ ...prev, selected, values }));
    }, [state.selected, state.values, deleteValue]);

    const getDatepicker = useCallback((data) => {
        const onDataPickerApplyHandler = (item) => onDataPickerApply(item, data.key);
        return (
            <DatePicker
                onApply={onDataPickerApplyHandler}
                className="btn form-control reactdaypicker-block"
                {...getValue(data)}
            />
        );
    }, [onDataPickerApply, getValue]);

    const getSelect = useCallback((select) => {
        return (
            <select
                onChange={onSelectChange}
                className="form-control long-input right"
                name={select.key}
                value={getValue(select)}
            >
                {fields[select.fieldIndex]?.data?.map((option, i) => (
                    <option key={i} value={option.value}>{option.title}</option>
                ))}
            </select>
        );
    }, [onSelectChange, getValue, fields]);

    const getDropdown = useCallback((dropdown) => {
        return (
            <DropdownList
                messages={messagesForSelect}
                className={'rw-dropdownlist-select'}
                dataKey='value'
                textField='title'
                placeholder={dropdown.placeholder || ''}
                data={fields[dropdown.fieldIndex]?.data || []}
                value={getValue(dropdown)}
                onChange={(value) => { onChange(value, dropdown.key); }}
            />
        );
    }, [messagesForSelect, getValue, fields, onChange]);

    const getMultiselect = useCallback((select) => {
        return (
            <Multiselect
                messages={messagesForSelect}
                placeholder={select.placeholder || ''}
                className='right'
                dataKey='value'
                textField='title'
                onChange={(value) => { onChange(value, select.key); }}
                data={fields[select.fieldIndex]?.data || []}
                name={select.key}
                value={getValue(select)}
            />
        );
    }, [messagesForSelect, onChange, fields, getValue]);

    const getTextInput = useCallback((data) => {
        return (
            <input
                className="form-control"
                type="text"
                name={data.key}
                onChange={onTextInputChange}
                placeholder={data.placeholder}
                value={getValue(data)}
            />
        );
    }, [onTextInputChange, getValue]);

    const getCustom = useCallback((data) => {
        const onChangeCustom = (item) => onChange(item, data.key);
        return (
            <Custom
                segment={data.label}
                values={getValue(data)}
                onAction={onChangeCustom}
            />
        );
    }, [onChange, getValue]);

    const getDynamic = useCallback((data) => {
        const onChangeDynamicHandler = (item) => onChangeDynamic(item, data.key);
        return (
            <DynamicSelectSegment
                segment={data.segment}
                multiselect={true}
                values={getValue(data)}
                onAction={onChangeDynamicHandler}
            />
        );
    }, [onChangeDynamic, getValue]);

    const getSelectGoalTree = useCallback((data) => {
        const onChangeHandler = (item) => onChange(item, data.key);
        return (
            <InputGroupMultiselect
                nesting={data.nesting}
                onChange={onChangeHandler}
                tags={getValue(data)}
            />
        );
    }, [onChange, getValue]);

    const getChangeFunction = useCallback((value, key, name) => {
        if (name === 'currency' || key === 'currency') {
            return onChange(value, key);
        } else if (name === 'datepicker') {
            return onDataPickerApply(value, key);
        } else {
            alert('No function assigned');
        }
    }, [onChange, onDataPickerApply]);

    const renderFilter = useCallback((item = {}, i) => {
        const onChangeFilterHandler = (item) => onChangeFilter(item, i);
        const onChangeDropdown = (i >= 0) ? onChangeFilterHandler : onAddFilter;
        const onDeleteHandler = () => onDelete(item, i);
        const templateMap = {
            getDatepicker,
            getSelect,
            getMultiselect,
            getDropdown,
            getTextInput,
            getCustom,
            getDynamic,
            getSelectGoalTree,
            getNull
        };
        return (
            <div className={`table-filter ${gray ? ' table-filter-gray' : ''}`} key={item.key}>
                {item.withoutDropdown ? null : (
                    <div className="table-filter__item">
                        <label>{item.dropdownLabel || staticNames.addFilter}</label>
                        <DropdownList
                            className={'rw-dropdownlist-select'}
                            placeholder={staticNames.addFilter}
                            dataKey='key'
                            textField='label'
                            data={fields}
                            disabled={item.disabled || state.selected}
                            value={fields.find(f => f.label === item.label) || null}
                            onChange={onChangeDropdown}
                        />
                    </div>
                )}
                {item.name ? (
                    <>
                        <div className="table-filter__item payouts-block">
                            {item.label && <label>{item.label}{'datepicker' !== item.name ? (<i className="fa fa-search" />) : null}</label>}
                            {templateMap[templateKeys[item.name]]?.(item)}
                        </div>
                        {!item.disabled || 'datepicker' === item.name ? (
                            <div className="table-filter__item control-buttons">
                                <Button
                                    loading={loading}
                                    extraClass={{ 'apply-btn': true }}
                                    text={staticNamesActions.apply}
                                    onClick={onActionHandler}
                                />
                                <Button
                                    extraClass={{ 'reset-btn': true, 'hidden': hideResetButton }}
                                    text={staticNamesActions.reset}
                                    onClick={onDeleteHandler}
                                />
                            </div>
                        ) : null}
                    </>
                ) : null}
                {item.withList ? (
                    <ButtonsList
                        fields={fields}
                        data={item}
                        onChange={(value, key) => getChangeFunction(value, key, item.name)}
                        currentValue={state.values[item.key]}
                        showSubtitle={item.showSubtitle}
                    />
                ) : null}
            </div>
        );
    }, [gray, staticNames.addFilter, fields, state.selected, state.values, loading, hideResetButton, staticNamesActions, onActionHandler, onAddFilter, onChangeFilter, onDelete, getChangeFunction]);

    useEffect(() => {
        const search = (location && location.search) || '';
        const parsedQuery = query.parse(search);

        let values = {
            page: parsedQuery.page || initPage,
            ...getDefValues(fields),
            ...parsedQuery
        };

        if ("/office/finances/report" !== location.pathname) {
            values.createStartDate = parsedQuery.startDate || startDate || moment().subtract(config.defaultDatepickerDaysPeriod, 'days');
            values.createEndDate = parsedQuery.endDate || endDate || moment().endOf('day');
        }

        setState({
            selected: getSelected(fields, values),
            values
        });
    }, [location, fields, startDate, endDate, getDefValues, getSelected]);

    useEffect(() => {
        onActionHandler('firstRender');
    }, [onActionHandler]);

    if (!fields.length) return null;
    
    return (
        <div className="ibox-title hidden">
            {state.selected.map(filter => renderFilter(filter))}
            {fields.length !== state.selected.length ? renderFilter() : null}
        </div>
    );
};

DataComponentFilter.propTypes = { 
    fields: PropTypes.array,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    gray: PropTypes.bool,
    loading: PropTypes.bool,
    hideResetButton: PropTypes.bool,
    onAction: PropTypes.func
};

DataComponentFilter.defaultProps = { 
    fields: [],
    startDate: null,
    endDate: null,
    gray: false,
    loading: false,
    hideResetButton: false,
    onAction: () => {}
};

export default DataComponentFilter;
